import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { PencilIcon, ChevronDownIcon, FileIcon, GithubIcon, LinkedinIcon, MailIcon } from "@/components/developer/DashboardIcons";
import { useState } from "react";
import PersonalDetailsModal from "./PersonalDetailsModal";
import ProfileImageModal from "./personal-details/ProfileImageModal";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { useToast } from "@/components/ui/use-toast";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const PersonalDetails = () => {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [jobSearchStatus, setJobSearchStatus] = useState("Actively looking");
  const { toast } = useToast();

  const { data: profile, isFetching, refetch } = useQuery({
    queryKey: ['developer-profile-details'],
    queryFn: async () => {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error('No user found');

      const { data, error } = await supabase
        .from('developer_profiles')
        .select('*')
        .eq('user_id', user.user.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        toast({
          title: "Error",
          description: "Failed to fetch profile data",
          variant: "destructive",
        });
        throw error;
      }

      return data;
    }
  });

  const handleImageUploaded = (url: string) => {
    refetch();
  };

  if (isFetching) {
    return (
      <Card className="p-6">
        <div className="flex items-start justify-between mb-4">
          <h2 className="text-xl font-semibold">Personal details</h2>
          <Skeleton className="h-9 w-9" />
        </div>
        
        <div className="space-y-6">
          <div className="flex items-center space-x-4">
            <Skeleton className="h-16 w-16 rounded-full" />
            <div className="space-y-2">
              <Skeleton className="h-5 w-32" />
              <Skeleton className="h-4 w-24" />
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <Skeleton className="h-4 w-16" />
              <Skeleton className="h-4 w-32" />
            </div>
            <div className="flex items-center space-x-2">
              <Skeleton className="h-4 w-16" />
              <Skeleton className="h-4 w-32" />
            </div>
            <div className="flex items-center space-x-2">
              <Skeleton className="h-4 w-16" />
              <Skeleton className="h-4 w-48" />
            </div>
            <div className="flex items-center space-x-2">
              <Skeleton className="h-4 w-16" />
              <Skeleton className="h-4 w-32" />
            </div>
          </div>

          <div className="space-y-2">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-10 w-full" />
          </div>

          <div className="space-y-2">
            <Skeleton className="h-4 w-32" />
            <div className="flex flex-wrap gap-2">
              <Skeleton className="h-6 w-20" />
              <Skeleton className="h-6 w-24" />
              <Skeleton className="h-6 w-16" />
            </div>
          </div>
        </div>
      </Card>
    );
  }

  const getFileNameFromUrl = (url: string) => {
    if (!url) return '';
    const segments = url.split('/');
    const fileName = segments[segments.length - 1];
    return decodeURIComponent(fileName);
  };

  return (
    <Card className="p-6">
      <div className="flex items-start justify-between mb-4">
        <h2 className="text-xl font-semibold">Personal details</h2>
        <Button variant="ghost" size="icon" onClick={() => setIsEditModalOpen(true)}>
          <PencilIcon className="h-4 w-4" />
        </Button>
      </div>
      
      <div className="space-y-4">
        <div className="flex items-center space-x-4">
          <div className="relative group">
            <Avatar 
              className="h-16 w-16 cursor-pointer transition-opacity duration-200 group-hover:opacity-75"
              onClick={() => setIsImageModalOpen(true)}
            >
              <AvatarImage src={profile?.profile_image_url} alt="Profile picture" />
              <AvatarFallback>
                {profile?.name ? profile.name.charAt(0).toUpperCase() : 'U'}
              </AvatarFallback>
              <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 text-white opacity-0 group-hover:opacity-100 rounded-full transition-opacity duration-200">
                <div style={{fontSize:"12px"}}>UPLOAD</div>
              </div>
            </Avatar>
          </div>
          <div>
            <h3 className="font-semibold">{profile?.name || 'Add your name'}</h3>
            <p className="text-sm text-gray-600">{profile?.title || 'Add your title'}</p>
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center space-x-2 text-sm">
            <MailIcon className="h-4 w-4 text-gray-500" />
            <span>{profile?.email || 'Add your email'}</span>
          </div>
          <div className="flex items-center space-x-2 text-sm">
            <GithubIcon className="h-4 w-4 text-gray-500" />
            {profile?.github_url?(
                <a 
                href={profile?.github_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 cursor-pointer"
              >
                GitHub Profile
              </a>
            ):(
              <div>Add GitHub profile</div>
            )}
          </div>
          <div className="flex items-center space-x-2 text-sm">
            <LinkedinIcon className="h-4 w-4 text-gray-500" />
            {profile?.linkedin_url?(
                <a 
                href={profile?.linkedin_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 cursor-pointer"
              >
                LinkedIn Profile
              </a>
            ):(
              <div>Add LinkedIn profile</div>
            )}
         </div>
        </div>

        <div className="space-y-2">
          <p className="text-sm">
            <span className="text-gray-600">Based in:</span> {profile?.location || 'Add location'}
          </p>
          <p className="text-sm">
            <span className="text-gray-600">Timezone:</span> {profile?.timezone || 'Add timezone'}
          </p>
          <p className="text-sm">
            <span className="text-gray-600">Available to work:</span> {profile?.available_from && profile?.available_to ? 
              `${profile.available_from} - ${profile.available_to}` : 
              'Add availability'
            }
          </p>
          <p className="text-sm">
            <span className="text-gray-600">Desired salary:</span> {profile?.salary ? 
              `US $${profile.salary} / year` : 
              'Add desired salary'
            }
          </p>
        </div>

        <div className="space-y-2">
          <p className="text-sm font-medium">Job search status</p>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="w-full justify-between">
                {jobSearchStatus}
                <ChevronDownIcon className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuItem onClick={() => setJobSearchStatus("Actively looking")}>
                Actively looking
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setJobSearchStatus("Open to offers")}>
                Open to offers
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setJobSearchStatus("Not looking")}>
                Not looking
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="space-y-2">
          <p className="text-sm font-medium">Job types you're open to</p>
          <div className="flex flex-wrap gap-2">
            {profile?.job_types && profile.job_types.length > 0 ? (
              profile.job_types.map((type) => (
                <Badge key={type} variant="outline">{type}</Badge>
              ))
            ) : (
              <Badge variant="outline">Add job types</Badge>
            )}
          </div>
        </div>

        {profile?.resume_url && (
          <div className="space-y-2">
            <p className="text-sm flex">
              <span className="text-gray-600 pr-2">CV file:</span>
              <a 
                href={profile.resume_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 inline-flex items-center"
              >
                <FileIcon className="h-4 w-4 mr-1" />
                {getFileNameFromUrl(profile.resume_url)}
              </a>
            </p>
          </div>
        )}
      </div>

      <PersonalDetailsModal 
        refetchSection={refetch}
        isOpen={isEditModalOpen}
        onOpenChange={setIsEditModalOpen}
        currentDetails={profile}
      />

      <ProfileImageModal
        isOpen={isImageModalOpen}
        onOpenChange={setIsImageModalOpen}
        onImageUploaded={handleImageUploaded}
      />
    </Card>
  );
};

export default PersonalDetails;